import axios from 'axios'
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
} from '../utils/storage'

const adminClient = axios.create({
  baseURL: '/api/admin',
  timeout: 10000,
})

adminClient.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let pendingRequests = []

const resolvePending = (token) => {
  pendingRequests.forEach(({ resolve, config }) => {
    config.headers.Authorization = `Bearer ${token}`
    resolve(adminClient(config))
  })
  pendingRequests = []
}

const rejectPending = (error) => {
  pendingRequests.forEach(({ reject }) => reject(error))
  pendingRequests = []
}

adminClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const refreshToken = getRefreshToken()

    if (
      error.response?.status === 401 &&
      refreshToken &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject, config: originalRequest })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const { data } = await axios.post('/api/v1/auth/refresh', {
          refreshToken,
        })
        const { accessToken, refreshToken: newRefreshToken } = data.data

        setTokens(accessToken, newRefreshToken)
        resolvePending(accessToken)

        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        return adminClient(originalRequest)
      } catch (refreshError) {
        rejectPending(refreshError)
        clearTokens()
        window.location.href = '/'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

const unwrapData = (res) => res.data?.data

export const getClientes = (params) =>
  adminClient.get('/clientes', { params }).then(unwrapData)

export const updateCliente = (id, data) =>
  adminClient.patch(`/clientes/${id}`, data).then(unwrapData)

export const uploadClienteLogo = (id, file) => {
  const formData = new FormData()
  formData.append('logo', file)

  return adminClient
    .patch(`/clientes/${id}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(unwrapData)
}

export const getUsuarios = (params) =>
  adminClient.get('/usuarios', { params }).then(unwrapData)

export const createUsuario = (data) =>
  adminClient.post('/usuarios', data).then(unwrapData)

export const inviteUsuario = (id, data = {}) =>
  adminClient.post(`/usuarios/${id}/invitacion`, data).then(unwrapData)

export const deleteUsuario = (id) =>
  adminClient.delete(`/usuarios/${id}`).then(unwrapData)
